import { SendBroadcastRequest } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { getAppCheckTokenHeaders, isFirebaseError } from '@/firebase';
import { useCompanyPermissions } from '@/companies/hooks';
import { useCompanyContext } from '@/companies/providers';
import { useBroadcastTranslation } from '../hooks';

/**
 * Everything the caller chooses per send. The company is not among them: this
 * hook is scoped to the company in context, which is also the company its
 * permission check asked about, so letting a caller name a different one could
 * only ever disagree with `isAllowed`.
 */
export type SendBroadcastVariables = Omit<SendBroadcastRequest, 'companyId'>;

type UseSendBroadcastActionData = {
    /** Whether this viewer can send a broadcast right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    sendBroadcast: (variables: SendBroadcastVariables) => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Sending an emergency broadcast: whether this viewer may do it, why not when
 * they may not, and the call itself. Kept together so a caller can wire up a
 * button — disabled state, tooltip, spinner and click — from one hook.
 *
 * *Which* teams may be broadcast to is not checked here: the server narrows
 * the team picker to the teams this viewer administers or guards, so there is
 * nothing to repeat client-side. `INVALID_TEAM` covers the race where that
 * changes between listing and sending.
 */
export function useSendBroadcastAction(): UseSendBroadcastActionData {
    const { t } = useBroadcastTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const { company } = useCompanyContext();
    const { canEditEmergencyBroadcast } = useCompanyPermissions();

    const [isAllowed, reason] = useMemo(() => {
        if (!canEditEmergencyBroadcast) {
            return [false, t('actions.send_broadcast.reason_not_permitted')];
        }

        return [true, ''];
    }, [canEditEmergencyBroadcast, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree.
    //
    // Every error `POST /v3/broadcasts` declares gets a branch — see the
    // `this.request<void, …>` union on `broadcasts.sendBroadcast` in
    // `api/generated/Api.ts`. The 401s are missing on purpose: the axios
    // interceptor refreshes and retries those before they reach here.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            // Thrown before the request, while minting the App Check token.
            if (isFirebaseError(error)) {
                return t('shared.errors.app_check_failed');
            }

            if (isApiError(error, 'APP_CHECK_FAILED')) {
                return t('shared.errors.app_check_failed');
            }
            if (isApiError(error, 'NOT_SUBSCRIBED')) {
                return t('shared.errors.not_subscribed');
            }
            if (isApiError(error, 'NO_SMS_SUBSCRIPTION')) {
                return t('shared.errors.no_sms_subscription');
            }
            if (isApiError(error, 'INVALID_TEAM')) {
                return t('actions.send_broadcast.error_invalid_team');
            }
            if (isApiError(error, 'COMPANY_NOT_FOUND')) {
                return t('actions.send_broadcast.error_company_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (variables: SendBroadcastVariables) =>
            // The broadcast endpoint is App Check protected, so the token has
            // to be fetched before the request rather than added by an
            // interceptor.
            getAppCheckTokenHeaders().then((headers) =>
                openApiRef.current.broadcasts
                    .sendBroadcast(
                        { ...variables, companyId: company.id },
                        { headers }
                    )
                    .then(() => {})
            ),
        // Nothing to write to the cache: a broadcast leaves no record the app
        // reads back.
        onSuccess: () => {
            UIToast.success(t('actions.send_broadcast.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        sendBroadcast: (variables) => mutation.mutateAsync(variables),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
